import { Box, Button } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTreeModifyContext } from "../TreeDataContext";
import { IndexableObject, isPropertyMandatory } from "../Helpers";
import { useConditionalContext } from "./ConditionalContext";
import { NewCheckInput } from "./NewCheckInput";
import { NewCheckInputControls } from "./NewCheckInputControls";

interface AddingCheckControlProps {
  allChecks: object;
  conditionArray: any[];
  path: string[];
}

export function AddingCheckControl(props: AddingCheckControlProps) {
  const { saveChangesAtPath, removeAtPath } = useTreeModifyContext();
  const { selectedCondition, conditions, unconditionedName } = useConditionalContext();

  const [addingCondition, setAddingCondition] = React.useState(false);
  const [currentKey, setCurrentKey] = React.useState("");
  const [currentValue, setCurrentValue] = React.useState("");
  const [canAdd, setCanAdd] = React.useState(false);

  useEffect(() => {
    let newConditions = Object.create(props.allChecks);
    if (newConditions[currentKey] === undefined) {
      setCanAdd(currentValue !== "" && currentKey !== "");
    } else {
      setCanAdd(false);
    }
  }, [currentKey, currentValue, props.allChecks]);

  const canAddCheckToProperty = useMemo(() => {
    if (selectedCondition === 0 && isPropertyMandatory(unconditionedName) && conditions!.length > 1) {
      return false;
    }
    return true;
  }, [conditions, selectedCondition, unconditionedName]);

  const canAddDefault = useMemo(() => {
    if (selectedCondition === 0 && !isPropertyMandatory(unconditionedName) && conditions!.length === 1) {
      if (conditions![0].condition === undefined) {
        return true;
      }
    }
    return false;
  }, [conditions, selectedCondition, unconditionedName]);

  const tryToAddCheck = useCallback(
    (newKey: string, newValue: Object, keepDefault?: boolean) => {
      if (newKey === "") {
        return;
      }

      setCurrentKey("");
      setCurrentValue("");

      if (keepDefault) {
        let allConditions = conditions!.slice(conditions![0].condition === undefined ? 1 : 0);
        let newCondition = Object.assign({}, conditions![0]);
        newCondition.condition = { [newKey]: newValue };
        allConditions.push(newCondition);
        const savePath = props.path.slice();
        savePath[savePath.length - 1] = "__cond_" + savePath[savePath.length - 1];
        saveChangesAtPath(allConditions, savePath);
        return;
      }

      if (selectedCondition === 0 && conditions![0].condition === undefined) {
        // Adding a check to an unconditioned property. Remove it and then add it as __cond_
        if (!isPropertyMandatory(unconditionedName)) {
          removeAtPath(props.path);
        }
        let allConditions = conditions!.slice();
        const savePath = props.path.slice();
        savePath[savePath.length - 1] = "__cond_" + savePath[savePath.length - 1];
        allConditions[0].condition = { [newKey]: newValue };
        saveChangesAtPath(allConditions, savePath);
        return;
      }

      let newConditions: IndexableObject = Object.assign({}, props.allChecks);
      newConditions[newKey] = newValue;
      saveChangesAtPath(newConditions, props.path);
    },
    [conditions, props.allChecks, props.path, removeAtPath, saveChangesAtPath, selectedCondition, unconditionedName],
  );

  const buttonText = useMemo(() => {
    if (selectedCondition === 0 && isPropertyMandatory(unconditionedName) && conditions!.length === 1) {
      return "Add conditional result";
    }
    if (conditions!.length === 1 && conditions![0].condition === undefined) {
      return "Make conditional";
    }
    return "Add condition";
  }, [conditions, selectedCondition, unconditionedName]);

  if (!canAddCheckToProperty) {
    return <></>;
  }

  return (
    <>
      <Box className={"inline-center"}>
        {!addingCondition && (
          <Button
            color={"success"}
            variant={"outlined"}
            onClick={() => setAddingCondition(true)}
            endIcon={<AddCircleIcon />}
          >
            {buttonText}
          </Button>
        )}
        {addingCondition && (
          <NewCheckInputControls
            canAdd={canAdd}
            canAddDefault={canAddDefault}
            onClickCancel={() => setAddingCondition(false)}
            onClickConfirm={() => {
              tryToAddCheck(currentKey, processOrValue(currentValue));
              setAddingCondition(false);
            }}
            onClickConfirmDefault={() => {
              tryToAddCheck(currentKey, processOrValue(currentValue), true);
              setAddingCondition(false);
            }}
          />
        )}
      </Box>
      {addingCondition && (
        <NewCheckInput
          checkKey={currentKey}
          checkValue={currentValue}
          onChangeKey={(event) => {
            setCurrentKey(event.target.value);
          }}
          onChangeValue={(event) => {
            setCurrentValue(event.target.value);
          }}
        />
      )}
    </>
  );
}

function processOrValue(value: string) {
  if (value.includes(",")) {
    return value.split(",").map((value) => value.trim());
  }
  return value;
}
